/**
 * What this is, said once, plainly.
 *
 * The page exists mostly for one distinction: slate is not a to-read list, and
 * someone who wants one should be pointed at an app that does it properly
 * rather than left to discover the mismatch over a fortnight. Hence the links
 * out, which are the honest kind of thing to put on an About page — not a
 * feature list.
 */

import { Install } from '../components/Install.tsx';

/** Apps that do the list job properly. Deliberately not framed as rivals. */
const LISTS = [
  { name: 'The StoryGraph', url: 'https://www.thestorygraph.com', what: 'books' },
  { name: 'Goodreads', url: 'https://www.goodreads.com', what: 'books' },
  { name: 'Hardcover', url: 'https://hardcover.app', what: 'books' },
  { name: 'Letterboxd', url: 'https://letterboxd.com', what: 'films' },
  { name: 'Trakt', url: 'https://trakt.tv', what: 'film and television' },
];

const SOURCES = [
  {
    name: 'Hardcover',
    url: 'https://hardcover.app',
    what: 'books, series and authors — dates, editions, blurbs and covers',
  },
  {
    name: 'TMDB',
    url: 'https://www.themoviedb.org',
    what: 'films and television, including dates by country',
  },
  {
    name: 'Wikipedia',
    url: 'https://en.wikipedia.org',
    what: 'the panel on an item’s page, under CC BY-SA',
  },
];

export function About({ onBack }: { onBack: () => void }) {
  return (
    <>
      <div className="appbar">
        <button className="wordmark" onClick={onBack}>
          ← slate
        </button>
      </div>

      <div className="verdict">
        <h1>About</h1>
      </div>

      <div className="aboutpage">
        <p>
          slate keeps track of books, tv series and movies that are not out yet. 
          Do you have to try remember all the series and authors you're waiting on each time you're looking for something new to read or watch? 
          <u>slate</u> does that for you.
        </p>
        <p>
          You add something in the moment you think of it, and then you can
          forget about it. When you open slate it answers two questions: what's available now, and has anything changed.
        </p>

        <h2>What it is not</h2>
        <p>
          It is not a list of things you want to read or watch. There are loads of great apps for that, a few are listed below.
          Some of those will also give you information about release dates, just not necessarily in the same way.
        </p>
        <p>
          Most of the things you add to your slate will have no date and no news, sometimes for
          years. That is the normal state rather than a gap to be filled, so
          nothing here nags and nothing expires. Things leave the list when you
          delete them and at no other time.
        </p>
        <p>Your list is your own. Nothing on it is shared or public.</p>

        <h2>A few list apps for books, series and movies</h2>
       
        <ul className="aboutlinks">
          {LISTS.map((site) => (
            <li key={site.url}>
              <a href={site.url} target="_blank" rel="noopener noreferrer">
                {site.name} ↗
              </a>
              <span> — {site.what}</span>
            </li>
          ))}
        </ul>

        <h2>Where the information comes from</h2>
        <ul className="aboutlinks">
          {SOURCES.map((source) => (
            <li key={source.url}>
              <a href={source.url} target="_blank" rel="noopener noreferrer">
                {source.name} ↗
              </a>
              <span> — {source.what}</span>
            </li>
          ))}
          <li>
            <span className="aboutplain">An author’s own site</span>
            <span> — when you have added one, read directly and feed first</span>
          </li>
        </ul>
        <p className="attribution attribution-flush">
          This product uses the TMDb API but is not endorsed or certified by
          TMDb. Book data from Hardcover. Wikipedia text is available under CC
          BY-SA.
        </p>

        <h2>On your phone</h2>
        <Install />
      </div>
    </>
  );
}
